// src/components/usePNGHandler.js
import { useState } from 'react';
import JSZip from 'jszip';
import usePNGSizeStore from '../store/usePNGSizeStore';

const usePNGHandler = (assets, accessToken, isLoggedIn, isPurchased, debug = false, archiveName) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const { userSelectedSizes, setSelectedSizeForCollection } = usePNGSizeStore();

  const getSizesForCollection = (collection) => {
    switch (collection) {
      case 'outline':
        return { all: [48, 64, 128, 256, 512], free: [48] };
      case 'polish-blue':
        return { all: [64, 128, 256, 512], free: [64] };
      case 'basic':
        return { all: [24, 32, 48, 64, 128, 256, 512], free: [24, 32, 48, 64, 128, 256, 512] };
      default:
        return { all: [], free: [] };
    }
  };

  const getDefaultSizeForCollection = (collection) => {
    switch (collection) {
      case 'outline': return 48;
      case 'polish-blue': return 64;
      case 'basic': return 24;
      default: return null;
    }
  };

  const singleAsset = assets && assets.length === 1 ? assets[0] : null;
  let collectionName = null;
  if (singleAsset) {
    collectionName = singleAsset.collection;
  } else if (assets && assets.length > 0) {
    collectionName = assets[0].collection;
  }

  const { all: dropdownSizes = [], free: freeSizes = [] } = collectionName 
    ? getSizesForCollection(collectionName) 
    : { all: [], free: [] };

  const savedSize = collectionName ? userSelectedSizes[collectionName] : null;
  let defaultSize = null;

  if (collectionName) {
    defaultSize = savedSize || getDefaultSizeForCollection(collectionName);
    if (!isPurchased && defaultSize && !freeSizes.includes(defaultSize)) {
      // Если пользователь не купил, но defaultSize недоступен, выбираем доступный бесплатный
      if (freeSizes.length > 0) {
        const collDef = getDefaultSizeForCollection(collectionName);
        defaultSize = freeSizes.includes(collDef) ? collDef : freeSizes[0];
      } else {
        const collDef = getDefaultSizeForCollection(collectionName);
        defaultSize = (collDef && freeSizes.includes(collDef)) ? collDef : (freeSizes[0] || null);
      }
    }
  }

  const saveSelectedSize = (collection, size) => {
    setSelectedSizeForCollection(collection, size);
  };

  const fetchPNGBlob = async (url, token) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(`Error fetching ${url}: ${response.statusText}`);
    return await response.blob();
  };

  const handleDownloadLogic = async (downloadSize) => {
    if (!assets || assets.length === 0) {
      throw new Error('No PNG assets available for download.');
    }
    if (!downloadSize) {
      throw new Error('Please select a size before downloading.');
    }

    // Проверка бесплатных размеров для всех ассетов
    const allFreeSizeForAllAssets = assets.every(asset => {
      const { free: assetFreeSizes } = getSizesForCollection(asset.collection);
      return assetFreeSizes.includes(downloadSize);
    });

    const requiresAuth = !allFreeSizeForAllAssets;
    if (requiresAuth && (!isLoggedIn || !accessToken)) {
      throw new Error('You must be logged in to download PNGs.');
    }

    if (!isPurchased && requiresAuth) {
      throw new Error(`You do not have access to size ${downloadSize}px. Purchase required.`);
    }

    // Один ассет
    if (assets.length === 1) {
      const asset = assets[0];
      const { slug, collection, format } = asset;
      if (!format.includes('png')) throw new Error(`Asset ${slug} is not a PNG.`);
      const url = `https://icons-pbl.davooda.com/${collection}/png-${downloadSize}px/${slug}-${downloadSize}x${downloadSize}.png`;
      const blob = await fetchPNGBlob(url, requiresAuth ? accessToken : null);
      return { type: 'single', blob, fileName: `${slug}-${downloadSize}.png` };
    }

    // Несколько ассетов — архив
    const zip = new JSZip();
    for (const asset of assets) {
      const { slug, collection, format } = asset;
      if (!format.includes('png')) continue;

      const { free } = getSizesForCollection(collection);
      if (!isPurchased && !free.includes(downloadSize)) {
        throw new Error(`You do not own this size. ${downloadSize}px is a paid size.`);
      }

      const url = `https://icons-pbl.davooda.com/${collection}/png-${downloadSize}px/${slug}-${downloadSize}x${downloadSize}.png`;
      const blob = await fetchPNGBlob(url, requiresAuth ? accessToken : null);
      const arrayBuffer = await blob.arrayBuffer();
      zip.file(`${slug}-${downloadSize}.png`, arrayBuffer);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const defaultArchiveName = archiveName || `davoodacom-pngs.zip`;
    return { type: 'archive', blob: zipBlob, fileName: defaultArchiveName };
  };

  return {
    isDownloading,
    setIsDownloading,
    errorMessage,
    setErrorMessage,
    dropdownSizes,
    freeSizes,
    defaultSize,
    handleDownloadLogic,
    saveSelectedSize,
    collectionName
  };
};

export default usePNGHandler;
